
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useTranslations } from "use-intl";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { UserRound, Building, CreditCard, LogOut, ShieldCheck, Plus, Loader2 } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useCompany } from "@/hooks/useCompany";
import { useCompanyList } from "@/hooks/useCompanyList";
import { PLATFORM_ADMIN_KEY, useSwitchCompany } from "@/hooks/useSwitchCompany";
import { CompanyPicker } from "./CompanyPicker";

interface UserMenuProps {
  /** `sidebar` is the Platform Admin View's footer slot — that layout has no top bar. */
  variant?: "topbar" | "sidebar";
}

export const UserMenu = ({ variant = "topbar" }: UserMenuProps) => {
  const inSidebar = variant === "sidebar";
  const { user, signOut, platformRole } = useAuth();
  const { company, isPlatformAdminView } = useCompany();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const companies = useCompanyList(open && pickerOpen);
  const { switchTo, pendingKey } = useSwitchCompany();
  const t = useTranslations("consultantShell.userMenu");
  const tCompany = useTranslations("consultantShell.changeCompany");
  const companyName = company?.name || (isPlatformAdminView ? tCompany("platformAdminView") : t("loading"));

  if (!user) return null;

  // Keep the menu open while a switch is in flight so the pending row stays visible;
  // close it once the switch has landed.
  const switchAndClose = async (companyId: string | null, name?: string) => {
    if (await switchTo(companyId, name)) setOpen(false);
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className={
            inSidebar
              ? "flex w-full items-center justify-start gap-2 px-3 py-2 text-muted-foreground hover:text-foreground"
              : "flex items-center gap-2 px-3 py-2 text-muted-foreground hover:text-foreground"
          }
          aria-label={t("label")}
        >
          <UserRound size={18} />
          <span className={inSidebar ? "text-sm font-medium truncate" : "text-sm font-medium hidden md:inline"}>{companyName}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent side={inSidebar ? "top" : "bottom"} align={inSidebar ? "start" : "end"}>
        <div className="px-4 py-2 border-b">
          <p className="text-sm font-medium" data-ph-mask>{user.email}</p>
          <p className="text-xs text-muted-foreground">{companyName}</p>
        </div>
        {platformRole === "platform_admin" && (
          <DropdownMenuItem
            disabled={isPlatformAdminView || pendingKey !== null}
            onSelect={(event) => {
              event.preventDefault();
              void switchAndClose(null);
            }}
            className="cursor-pointer flex items-center gap-2"
          >
            {pendingKey === PLATFORM_ADMIN_KEY ? <Loader2 size={16} className="animate-spin" /> : <ShieldCheck size={16} />}
            <span>{t("platformAdmin")}</span>
            {isPlatformAdminView && <span className="ml-auto text-xs text-muted-foreground">{tCompany("current")}</span>}
          </DropdownMenuItem>
        )}
        <DropdownMenuSub onOpenChange={setPickerOpen}>
          <DropdownMenuSubTrigger className="cursor-pointer flex items-center gap-2">
            <Building size={16} />
            <span>{t("changeCompany")}</span>
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent className="p-0">
            <CompanyPicker
              companies={companies.data}
              isLoading={companies.isPending}
              isError={companies.isError}
              onRetry={() => void companies.refetch()}
              currentCompanyId={company?.id}
              pendingCompanyId={pendingKey === PLATFORM_ADMIN_KEY ? null : pendingKey}
              switching={pendingKey !== null}
              onSelect={(selected) => void switchAndClose(selected.id, selected.name)}
            />
            <DropdownMenuSeparator className="my-0" />
            <DropdownMenuItem
              onSelect={() => navigate("/join-company")}
              className="m-1 cursor-pointer flex items-center gap-2"
            >
              <Plus size={16} />
              <span>{t("createCompany")}</span>
            </DropdownMenuItem>
          </DropdownMenuSubContent>
        </DropdownMenuSub>
        <DropdownMenuItem asChild>
          <Link to="/profile" className="cursor-pointer flex items-center gap-2">
            <UserRound size={16} />
            <span>{t("profile")}</span>
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to="/account-billing" className="cursor-pointer flex items-center gap-2">
            <CreditCard size={16} />
            <span>{t("billing")}</span>
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => signOut()} className="cursor-pointer text-red-500 hover:text-red-500 flex items-center gap-2">
          <LogOut size={16} />
          <span>{t("signOut")}</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
